/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { ApiRootActions, ApiRootLinkRel, ApiRootProps, ApiRootResource, ApiRootSelectors } from '@common/api-root-shared';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { hasLink } from '@ngxp/rest';
import { catchError, filter, map, Observable, of, switchMap } from 'rxjs';
import { HintListResource } from '../hint.model';
import { HintActions } from './hint.actions';
import { HintRepository } from './hint.repository';

@Injectable()
export class HintEffects {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly repository = inject(HintRepository);

  initLoadList$: Observable<Action> = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(ApiRootActions.loadApiRootSuccess),
        filter((props: ApiRootProps): boolean => hasLink(props.apiRoot, ApiRootLinkRel.HINTS)),
        switchMap((props: ApiRootProps): Observable<Action> => this.loadList(props.apiRoot)),
      ),
  );

  refreshList$: Observable<Action> = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(HintActions.refreshHintList),
        concatLatestFrom(() => this.store.select(ApiRootSelectors.apiRoot)),
        filter(([, apiRoot]): boolean => hasLink(apiRoot.resource, ApiRootLinkRel.HINTS)),
        switchMap(([, apiRoot]): Observable<Action> => this.loadList(apiRoot.resource)),
      ),
  );

  loadList(apiRoot: ApiRootResource): Observable<Action> {
    return this.repository.loadList(apiRoot).pipe(
      map((hintList: HintListResource): Action => HintActions.loadHintListSuccess({ hintList })),
      catchError(
        (error: HttpErrorResponse): Observable<Action> =>
          of(HintActions.loadHintListFailed({ apiError: error.error })),
      ),
    );
  }
}
